import type { CharacterSheet, Provenance, SheetSpell } from '@rollcall/contracts'
import type { TFunction } from 'i18next'
import { ABILITY_KEYS, ABILITY_NAMES } from '@rollcall/rules'
import { characterSubtitle } from './heading.js'
import { DETAIL_FIELDS } from './ui/details.js'
import {
  contentName,
  featureName,
  resourceName,
  traitLabel,
  valueLabel,
} from './ui/labels.js'

/**
 * Лист персонажа текстом (D54).
 *
 * Только наружу: распечатать, положить в заметки, кинуть в чат.
 * Обратно этот текст не разбирается и разбираться не будет — для
 * возврата есть JSON с журналом. Разбор текста, свёрстанного для
 * чтения, — это угадывание, а угаданный персонаж хуже, чем никакого.
 *
 * Ни одного расчёта здесь нет, как и на самом листе: числа приходят
 * свёрткой с сервера (D4), а этот файл только раскладывает их
 * по разделам. Величина, которой не нашлось раздела, попадает
 * в «прочее»: молча потерянное число хуже некрасивой строки — и это
 * же свойство проверяется тестом «в тексте есть все числа листа».
 *
 * Тексты особенностей — материал SRD (D7), поэтому внизу стоит
 * указание источника: файл уходит из приложения и живёт дальше сам.
 */

/** Порядок сводки — тот же, что на вкладке «Персонаж». */
const SUMMARY = [
  'ac',
  'hp.max',
  'initiative',
  'speed',
  'proficiency_bonus',
  'senses.darkvision',
] as const

const TRAIT_SETS = [
  'proficiencies',
  'languages',
  'resistances',
  'advantages',
  'disadvantages',
] as const

const SPELL_VALUES = ['spell.save_dc', 'spell.attack'] as const

const SIGNED = new Set<string>(['initiative', 'proficiency_bonus', 'spell.attack'])

function signed(value: number): string {
  return value >= 0 ? `+${value}` : String(value)
}

function numberOf(value: Provenance | undefined): number | null {
  return typeof value?.value === 'number' ? value.value : null
}

function traitsOf(value: Provenance | undefined): readonly string[] {
  return Array.isArray(value?.value) ? value.value : []
}

function shownValue(path: string, provenance: Provenance): string {
  if (Array.isArray(provenance.value)) return provenance.value.join(', ')
  if (typeof provenance.value !== 'number') return String(provenance.value)
  return SIGNED.has(path) ? signed(provenance.value) : String(provenance.value)
}

function spellLine(t: TFunction, spell: SheetSpell): string {
  const name = contentName(t, spell.id, spell.name)
  return spell.level === 0
    ? `- ${name}`
    : `- ${name} (${t('markdown.spellLevel', { level: spell.level })})`
}

export function sheetMarkdown(t: TFunction, sheet: CharacterSheet): string {
  const lines: string[] = []
  // Величины, которые уже показаны разделом: остальное уйдёт в «прочее».
  const shown = new Set<string>()

  const put = (path: string, label?: string): void => {
    const provenance = sheet.values[path]
    if (provenance === undefined) return
    shown.add(path)
    lines.push(
      `- **${label ?? valueLabel(t, path) ?? path}:** ${shownValue(path, provenance)}`,
    )
  }

  lines.push(`# ${sheet.character.name}`, '')
  lines.push(characterSubtitle(t, sheet), '')
  lines.push(`${t('sheet.code')}: \`${sheet.character.code}\``, '')

  /* ── Сводка ────────────────────────────────────────────────────── */
  lines.push(`## ${t('markdown.summary')}`, '')
  for (const path of SUMMARY) put(path)
  const hp = sheet.combat.hp
  lines.push(
    `- **${t('combat.hp')}:** ${hp.current}/${hp.max}` +
      (hp.temp > 0 ? ` (+${hp.temp} ${t('combat.temp')})` : ''),
  )
  if (hp.hitDie !== null) {
    lines.push(
      `- **${t('combat.hitDie')}:** ${hp.hitDice - hp.hitDiceSpent}/${hp.hitDice} ${hp.hitDie}`,
    )
  }
  lines.push(
    `- **${t('markdown.xp')}:** ${sheet.xp.total}`,
    `- **${t('sheet.level')}:** ${sheet.level}`,
  )
  shown.add('level')
  lines.push('')

  /* ── Характеристики ────────────────────────────────────────────── */
  lines.push(`## ${t('sheet.abilities')}`, '')
  lines.push(
    `| ${t('markdown.ability')} | ${t('markdown.score')} | ${t('markdown.mod')} |`,
  )
  lines.push('| --- | --- | --- |')
  for (const name of ABILITY_NAMES) {
    const key = ABILITY_KEYS[name]
    const score = sheet.context.abilities[key]
    shown.add(`abilities.${key}.score`)
    shown.add(`check.${key}`)
    const check = numberOf(sheet.values[`check.${key}`])
    lines.push(
      `| ${t(`abilities.${name}`)} | ${score.score} | ${signed(check ?? score.mod)} |`,
    )
  }
  lines.push('')

  /* ── Спасброски и навыки ───────────────────────────────────────── */
  lines.push(`## ${t('sheet.saves')}`, '')
  for (const save of sheet.saves) {
    shown.add(save.path)
    const value = numberOf(sheet.values[save.path])
    lines.push(
      `- ${t(`abilities.${save.ability}`)}: ${signed(value ?? 0)}` +
        (save.proficient ? ` · ${t('sheet.proficient')}` : ''),
    )
  }
  lines.push('')

  lines.push(`## ${t('sheet.skills')}`, '')
  for (const skill of sheet.skills) {
    shown.add(skill.path)
    const value = numberOf(sheet.values[skill.path])
    lines.push(
      `- ${contentName(t, skill.id, skill.name)}: ${signed(value ?? 0)}` +
        (skill.proficient ? ` · ${t('sheet.proficient')}` : ''),
    )
  }
  const passive = sheet.values['passive.perception']
  if (passive !== undefined) put('passive.perception')
  lines.push('')

  /* ── Владения, языки, сопротивления ────────────────────────────── */
  const traitSections = TRAIT_SETS.filter(
    (path) => traitsOf(sheet.values[path]).length > 0,
  )
  if (traitSections.length > 0) {
    lines.push(`## ${t('sheet.traits')}`, '')
    for (const path of traitSections) {
      shown.add(path)
      const traits = traitsOf(sheet.values[path])
        .map((trait) => traitLabel(t, path, trait))
        .join(', ')
      lines.push(`- **${valueLabel(t, path) ?? path}:** ${traits}`)
    }
    lines.push('')
  }

  /* ── Бой ───────────────────────────────────────────────────────── */
  if (sheet.combat.attacks.length > 0) {
    lines.push(`## ${t('tabs.combat')}`, '')
    lines.push(
      `| ${t('combat.attack')} | ${t('markdown.hit')} | ${t('combat.damage')} |`,
    )
    lines.push('| --- | --- | --- |')
    for (const attack of sheet.combat.attacks) {
      shown.add(attack.attackPath)
      const bonus = numberOf(sheet.values[attack.attackPath])
      let damage = attack.damage.notation
      if (attack.damagePath !== null) {
        shown.add(attack.damagePath)
        const extra = numberOf(sheet.values[attack.damagePath])
        if (extra !== null && extra !== 0) damage += ` ${signed(extra)}`
      }
      lines.push(
        `| ${contentName(t, attack.id, attack.name)} | ${signed(bonus ?? 0)} | ${damage} ${attack.damage.type} |`,
      )
    }
    lines.push('')
  }

  /* ── Заклинания ────────────────────────────────────────────────── */
  if (sheet.spells.caster) {
    lines.push(`## ${t('tabs.spells')}`, '')
    for (const path of SPELL_VALUES) put(path)
    const slots = sheet.spells.slots
      .map((slot) => `${slot.level}: ${slot.total - slot.used}/${slot.total}`)
      .join(' · ')
    if (slots !== '') lines.push(`- **${t('spells.slots')}:** ${slots}`)
    lines.push('')
    if (sheet.spells.cantrips.length > 0) {
      lines.push(`### ${t('spells.cantrips')}`, '')
      for (const spell of sheet.spells.cantrips) lines.push(spellLine(t, spell))
      lines.push('')
    }
    if (sheet.spells.prepared.length > 0) {
      lines.push(`### ${sheet.spells.label ?? t('spells.prepared')}`, '')
      for (const spell of sheet.spells.prepared) lines.push(spellLine(t, spell))
      lines.push('')
    }
  }

  /* ── Инвентарь ─────────────────────────────────────────────────── */
  lines.push(`## ${t('tabs.inventory')}`, '')
  for (const entry of sheet.inventory.entries) {
    const name = entry.id === null ? entry.name : contentName(t, entry.id, entry.name)
    const count = entry.quantity > 1 ? ` ×${entry.quantity}` : ''
    const worn = entry.equipped ? ` · ${t('markdown.equipped')}` : ''
    lines.push(`- ${name}${count}${worn}`)
  }
  lines.push(
    `- **${t('markdown.coins')}:** ${sheet.inventory.coinsGp} gp`,
    `- **${t('markdown.weight')}:** ${sheet.inventory.totalWeightLb} / ${sheet.inventory.carryLb} lb`,
    '',
  )

  /* ── Счётчики ресурсов (D56) ───────────────────────────────────── */
  if (sheet.resources.length > 0) {
    lines.push(`## ${t('markdown.resources')}`, '')
    for (const resource of sheet.resources) {
      lines.push(
        // Имя — как на листе: «Божественный канал», а не «Channel Divinity».
        `- **${resourceName(t, resource)}:** ` +
          t('resources.count', { current: resource.current, max: resource.max }),
      )
    }
    lines.push('')
  }

  /* ── Особенности ───────────────────────────────────────────────── */
  if (sheet.features.length > 0) {
    lines.push(`## ${t('sheet.features')}`, '')
    for (const feature of sheet.features) {
      lines.push(`### ${featureName(t, feature.key, feature.name)}`, '')
      lines.push(feature.text.trim(), '')
    }
  }

  /* ── Описание персонажа (D4) ───────────────────────────────────── */
  const described = DETAIL_FIELDS.filter(
    (field) => sheet.character.details[field].trim() !== '',
  )
  if (described.length > 0) {
    lines.push(`## ${t('details.title')}`, '')
    for (const field of described) {
      lines.push(`### ${t(`details.${field}`)}`, '')
      lines.push(sheet.character.details[field].trim(), '')
    }
  }

  /* ── Состояния ─────────────────────────────────────────────────── */
  if (sheet.conditions.length > 0) {
    lines.push(`## ${t('markdown.conditions')}`, '')
    for (const condition of sheet.conditions) {
      lines.push(
        `- ${contentName(t, condition.id, condition.name)}` +
          (condition.level === null ? '' : ` (${condition.level})`),
      )
    }
    lines.push('')
  }

  /* ── Прочие величины ───────────────────────────────────────────── */
  const rest = Object.keys(sheet.values).filter((path) => !shown.has(path))
  if (rest.length > 0) {
    lines.push(`## ${t('sheet.rest')}`, '')
    for (const path of rest.sort()) put(path)
    lines.push('')
  }

  /* ── Источник материалов (D7) ──────────────────────────────────── */
  lines.push('---', '', t('markdown.attribution'), '')

  return lines.join('\n')
}
